import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AutoChatbot = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('es');
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Conversation history states
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  
  // Check if mobile/tablet
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
  
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      // Auto-show history on desktop only
      if (width >= 1024) {
        setShowHistory(true);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize(); // Call on mount
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const languages = [
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
  ];

  const featureDescriptions = {
    'text_analysis': { icon: '📊', name: 'Análisis', desc: 'Contenido profundo' },
    'sentiment': { icon: '😊', name: 'Sentimiento', desc: 'Emociones y tono' },
    'code_certification': { icon: '✅', name: 'Código', desc: 'Limpio con tests' },
    'metrics': { icon: '📈', name: 'Métricas', desc: 'Estadísticas' },
    'research': { icon: '🔬', name: 'Investigación', desc: 'Búsqueda exhaustiva' },
    'advanced_graphs': { icon: '📉', name: 'Gráficos', desc: 'Visualizaciones' }
  };

  useEffect(() => {
    loadCapabilities();
    loadConversations();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadCapabilities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/chatbot/capabilities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCapabilities(response.data);
      
      if (response.data.tier === 'advanced' || response.data.tier === 'elite') {
        const availableFeatures = response.data.available_features.filter(f => 
          ['text_analysis', 'sentiment', 'code_certification', 'metrics', 'research', 'advanced_graphs'].includes(f)
        );
        setSelectedFeatures(availableFeatures);
      }
    } catch (error) {
      console.error('Error loading capabilities:', error);
    }
  };

  const loadConversations = async () => {
    try {
      setLoadingConversations(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  };

  const createNewConversation = async (title = null) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/conversations`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { title: title || 'Nueva conversación' }
        }
      );
      const newConv = response.data.conversation;
      setCurrentConversationId(newConv.id);
      setSessionId(newConv.id);
      setMessages([]);
      setConversations(prev => [newConv, ...prev]);
      return newConv.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      const fallbackId = `local_${Date.now()}`;
      setSessionId(fallbackId);
      setCurrentConversationId(null);
      return fallbackId;
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/chat/conversations/${conversationId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCurrentConversationId(conversationId);
      setSessionId(conversationId);
      
      const loadedMessages = (response.data.messages || []).map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.created_at,
        model: msg.model
      }));
      
      setMessages(loadedMessages);
      // Close history panel on mobile after selecting
      if (isMobile) setShowHistory(false);
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar esta conversación?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/chat/conversations/${conversationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      
      if (currentConversationId === conversationId) {
        setCurrentConversationId(null);
        setMessages([]);
        setSessionId(null);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const startNewChat = async () => {
    await createNewConversation();
    if (isMobile) setShowHistory(false);
  };

  const toggleFeature = (feature) => {
    if (selectedFeatures.includes(feature)) {
      setSelectedFeatures(selectedFeatures.filter(f => f !== feature));
    } else {
      setSelectedFeatures([...selectedFeatures, feature]);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const messageText = inputMessage;
    setInputMessage('');
    setLoading(true);

    let convId = currentConversationId || sessionId;
    if (!convId) {
      convId = await createNewConversation(messageText.substring(0, 50));
    }

    const userMsg = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);

    try {
      const token = localStorage.getItem('token');
      
      const context = messages.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await axios.post(
        `${BACKEND_URL}/api/chatbot/message`,
        {
          message: messageText,
          session_id: convId,
          language: selectedLanguage,
          features: selectedFeatures.length > 0 ? selectedFeatures : undefined,
          context: context.length > 0 ? context : undefined
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const assistantMsg = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        model: response.data.model,
        tier: response.data.tier,
        tier_name: response.data.tier_name,
        capabilities_used: response.data.capabilities_used || [],
        text_analysis: response.data.text_analysis,
        sentiment: response.data.sentiment,
        metrics: response.data.metrics,
        code_certification: response.data.code_certification,
        certification: response.data.certification
      };
      
      setMessages(prev => [...prev, assistantMsg]);

      if (conversations.length > 0 && conversations[0].message_count === 0) {
        setConversations(prev => {
          const updated = [...prev];
          if (updated[0]) {
            updated[0] = { ...updated[0], title: messageText.substring(0, 50), message_count: 2 };
          }
          return updated;
        });
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg = {
        role: 'system',
        content: `Error: ${error.response?.data?.detail || 'No se pudo procesar el mensaje'}`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setCurrentConversationId(null);
    setSessionId(null);
  };

  const getTierBadge = () => {
    if (!capabilities) return null;
    
    const tierColors = {
      'basic': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      'standard': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      'advanced': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      'elite': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
    };

    const tierNames = {
      'basic': '🔵 Básico',
      'standard': '🟢 Estándar',
      'advanced': '⭐ Avanzado',
      'elite': '⭐ Elite'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${tierColors[capabilities.tier] || tierColors.basic}`}>
        {tierNames[capabilities.tier] || '🔵 Básico'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
  };

  // Get tier description text
  const getTierDescription = () => {
    if (!capabilities) return 'Cargando...';
    
    if (capabilities.tier === 'advanced' || capabilities.tier === 'elite') {
      return 'Versión Avanzada con análisis avanzado, código certificado, investigación profunda y 5 idiomas.';
    } else if (capabilities.tier === 'standard') {
      return 'Versión Estándar con análisis moderado, código básico y 4 idiomas.';
    }
    return 'Versión Básica con respuestas rápidas y precisas en 3 idiomas.';
  };

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen flex flex-col">
      <div className="px-4 sm:px-6 lg:px-10">
        <Header />
      </div>

      <div className="flex-1 flex flex-col lg:flex-row max-w-7xl w-full mx-auto px-2 sm:px-4 lg:px-8 py-4 gap-4">
        
        {/* Mobile/Tablet: Top Action Bar */}
        {(isMobile || isTablet) && (
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => setShowHistory(!showHistory)}
              data-testid="toggle-history-btn"
              className="flex-1 py-2.5 px-4 bg-white dark:bg-[#1a2530] rounded-lg border border-gray-200 dark:border-[#283039] text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center justify-center gap-2"
            >
              {showHistory ? '✕ Cerrar' : '📜 Historial'}
            </button>
            <button
              onClick={startNewChat}
              data-testid="new-chat-btn"
              className="py-2.5 px-4 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              ➕ Nuevo Chat
            </button>
          </div>
        )}

        {/* History Panel - Slide overlay on mobile, sidebar on desktop */}
        {showHistory && (
          <div className={`
            ${isMobile || isTablet 
              ? 'fixed inset-0 z-50 bg-black/50 flex' 
              : 'w-64 lg:w-72 flex-shrink-0'}
          `}>
            {/* Backdrop click to close on mobile */}
            {(isMobile || isTablet) && (
              <div 
                className="absolute inset-0" 
                onClick={() => setShowHistory(false)}
              />
            )}
            
            <div className={`
              bg-white dark:bg-[#1a2530] 
              ${isMobile || isTablet 
                ? 'relative w-[85%] max-w-sm h-full ml-auto shadow-xl animate-slide-in-right' 
                : 'rounded-lg border border-gray-200 dark:border-[#283039] h-fit max-h-[calc(100vh-200px)]'}
              flex flex-col
            `}>
              {/* Header */}
              <div className="p-4 border-b border-gray-200 dark:border-[#283039]">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    📜 Historial
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => loadConversations()}
                      className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-1"
                      title="Recargar"
                    >
                      🔄
                    </button>
                    {(isMobile || isTablet) && (
                      <button
                        onClick={() => setShowHistory(false)}
                        className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-1 text-lg"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
                <button
                  onClick={startNewChat}
                  data-testid="sidebar-new-chat-btn"
                  className="w-full py-2.5 px-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                >
                  ➕ Nueva Conversación
                </button>
              </div>

              {/* Conversation List */}
              <div className="flex-1 overflow-y-auto p-3">
                {loadingConversations ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="animate-spin inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full mb-2" />
                    <p className="text-sm">Cargando...</p>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <span className="text-3xl block mb-2">💬</span>
                    <p className="text-sm">No hay conversaciones</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {conversations.map((conv) => (
                      <div
                        key={conv.id}
                        onClick={() => loadConversation(conv.id)}
                        data-testid={`conversation-${conv.id}`}
                        className={`p-3 rounded-lg cursor-pointer transition-colors group ${
                          currentConversationId === conv.id
                            ? 'bg-primary/10 border border-primary/30'
                            : 'hover:bg-gray-100 dark:hover:bg-[#283039]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {conv.title || 'Nueva conversación'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {formatDate(conv.updated_at || conv.created_at)}
                            </p>
                          </div>
                          <button
                            onClick={(e) => deleteConversation(conv.id, e)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity p-1"
                            title="Eliminar"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header del Chatbot */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {!isMobile && !isTablet && (
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-[#283039] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#3b4754]"
                    title={showHistory ? 'Ocultar historial' : 'Mostrar historial'}
                  >
                    {showHistory ? '◀️' : '▶️'}
                  </button>
                )}
                <div>
                  <h1 className="text-base sm:text-lg lg:text-xl font-black text-gray-900 dark:text-white flex items-center gap-2">
                    🤖 Chatbot Automatizado
                  </h1>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    GPT-JDP AI Assistant
                  </p>
                </div>
              </div>
              {capabilities && getTierBadge()}
            </div>

            {/* Capabilities Info - Responsive Grid */}
            {capabilities && (
              <div className="bg-white dark:bg-[#1a2530] rounded-lg p-3 border border-gray-200 dark:border-[#283039] mb-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs sm:text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 block">Modelo</span>
                    <span className="font-semibold text-gray-900 dark:text-white text-xs">
                      {capabilities.models?.[0] || 'GPT-5'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 block">Nivel</span>
                    <span className="font-semibold text-gray-900 dark:text-white capitalize text-xs">
                      {capabilities.tier_name || capabilities.tier}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 block">Costo</span>
                    <span className="font-semibold text-gray-900 dark:text-white text-xs">
                      {capabilities.credit_cost}/msg
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 block">Créditos</span>
                    <span className="font-semibold text-primary text-xs">
                      {user?.credits?.toLocaleString() || '∞'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Language Selector - Scrollable on mobile */}
            <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-2">
              <span className="text-xs text-gray-600 dark:text-gray-400 flex-shrink-0">Idioma:</span>
              <div className="flex gap-1.5">
                {languages.map((lang) => {
                  const isAvailable = capabilities?.languages?.includes(lang.code);
                  
                  return (
                    <button
                      key={lang.code}
                      onClick={() => isAvailable && setSelectedLanguage(lang.code)}
                      disabled={!isAvailable}
                      className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors flex-shrink-0 ${
                        selectedLanguage === lang.code
                          ? 'bg-primary text-white'
                          : isAvailable
                          ? 'bg-gray-100 dark:bg-[#283039] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#3b4754]'
                          : 'bg-gray-50 dark:bg-[#1a2530] text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      }`}
                      title={!isAvailable ? 'No disponible en tu plan' : ''}
                    >
                      {lang.flag} {isMobile ? '' : lang.name}
                      {!isAvailable && ' 🔒'}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Features Selector - Compact grid for mobile */}
            {(capabilities?.tier === 'advanced' || capabilities?.tier === 'elite') && capabilities.advanced_features && (
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-purple-900 dark:text-purple-300">
                    ⭐ Características Avanzadas
                  </h3>
                  <button
                    onClick={() => {
                      if (selectedFeatures.length > 0) {
                        setSelectedFeatures([]);
                      } else {
                        const allFeatures = Object.keys(featureDescriptions);
                        setSelectedFeatures(allFeatures);
                      }
                    }}
                    className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
                  >
                    {selectedFeatures.length > 0 ? 'Ninguna' : 'Todas'}
                  </button>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
                  {Object.entries(featureDescriptions).map(([key, feature]) => (
                    <button
                      key={key}
                      onClick={() => toggleFeature(key)}
                      className={`p-2 rounded-lg text-center transition-colors ${
                        selectedFeatures.includes(key)
                          ? 'bg-purple-200 dark:bg-purple-800 border-2 border-purple-400 dark:border-purple-600'
                          : 'bg-white dark:bg-[#1a2530] border border-gray-200 dark:border-[#283039] hover:border-purple-300 dark:hover:border-purple-700'
                      }`}
                    >
                      <span className="text-lg block">{feature.icon}</span>
                      <span className="text-xs font-medium text-gray-900 dark:text-white block truncate">
                        {feature.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Chat Messages */}
          <div className="flex-1 bg-white dark:bg-[#1a2530] rounded-lg border border-gray-200 dark:border-[#283039] flex flex-col overflow-hidden min-h-[300px] sm:min-h-[400px]">
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center py-8">
                  <span className="text-5xl text-gray-300 dark:text-gray-600 mb-3">
                    🤖
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Chatbot GPT-JDP
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md px-4">
                    {getTierDescription()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-3">
                    Escribe tu mensaje para comenzar
                  </p>
                </div>
              )}

              {messages.map((msg, index) => (
                <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] sm:max-w-[75%] ${msg.role === 'user' ? 'ml-4' : 'mr-4'}`}>
                    <div
                      className={`rounded-lg p-3 ${
                        msg.role === 'user'
                          ? 'bg-primary text-white'
                          : msg.role === 'system'
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-300 dark:border-red-800'
                          : 'bg-gray-100 dark:bg-[#283039] text-gray-900 dark:text-white'
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words text-sm">{msg.content}</p>
                      
                      {/* Show extra data for advanced messages */}
                      {msg.role === 'assistant' && msg.capabilities_used && msg.capabilities_used.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
                          <div className="text-xs font-semibold mb-1 opacity-75">
                            Capacidades:
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {msg.capabilities_used.map((cap, i) => (
                              <span key={i} className="px-1.5 py-0.5 bg-white/20 dark:bg-black/20 rounded text-xs">
                                {cap}
                              </span>
                            ))}
                          </div>
                          
                          {msg.sentiment && (
                            <div className="mt-1 text-xs">
                              <strong>Sentimiento:</strong> {msg.sentiment.sentiment} ({Math.round(msg.sentiment.confidence * 100)}%)
                            </div>
                          )}
                          
                          {msg.metrics && (
                            <div className="mt-1 text-xs">
                              <strong>Métricas:</strong> {msg.metrics.output_words} palabras
                            </div>
                          )}
                          
                          {msg.certification && (
                            <div className="mt-1 text-xs flex items-center gap-1 text-green-600 dark:text-green-400">
                              <span>🏆</span>
                              <strong>Certificado:</strong> {msg.certification.certificate_id}
                            </div>
                          )}
                          
                          {msg.code_certification && msg.code_certification.certified && (
                            <div className="mt-1 text-xs flex items-center gap-1">
                              <span>✅</span>
                              <strong>Código Certificado</strong>
                              <span className="opacity-75">- {msg.code_certification.quality_score}/100</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {msg.role === 'assistant' && msg.model && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 px-1">
                        {msg.model} • {msg.tier_name || msg.tier}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 dark:bg-[#283039] rounded-lg p-3 max-w-[75%]">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Procesando...
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area - Fixed at bottom */}
            <div className="border-t border-gray-200 dark:border-[#283039] p-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Escribe tu mensaje..."
                  rows={isMobile ? 2 : 3}
                  data-testid="chat-input"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-[#3b4754] rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-[#283039] text-gray-900 dark:text-white resize-none text-sm"
                  disabled={loading}
                />
                <div className="flex flex-col gap-2">
                  <button
                    onClick={sendMessage}
                    disabled={loading || !inputMessage.trim()}
                    data-testid="send-message-btn"
                    className="p-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    📤
                  </button>
                  {messages.length > 0 && (
                    <button
                      onClick={clearChat}
                      data-testid="clear-chat-btn"
                      className="p-3 bg-gray-200 dark:bg-[#283039] text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-[#3b4754] transition-colors"
                      title="Limpiar chat"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 hidden sm:block">
                Enter para enviar, Shift+Enter para nueva línea
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-10 mt-4">
        <Footer />
      </div>

      {/* CSS for slide animation */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AutoChatbot;
